import { createId } from "../../app/runtimeContext";
import type { ObservabilityClient } from "../../observability/types";
import type { EventBus } from "../events/eventBus";
import type { RequestRuntimeContext } from "../context/runtimeContext";

export type AgentStatus =
  "idle" | "queued" | "running" | "completed" | "failed" | "paused";
export type AgentPermission = { name: string; allowed: boolean };
export type AgentMemoryReference = { memoryId: string; reason: string };
export type AgentToolReference = { toolName: string; permission: string };

export type Agent = {
  id: string;
  name: string;
  status: AgentStatus;
  permissions: AgentPermission[];
  memory: AgentMemoryReference[];
  tools: AgentToolReference[];
};

export type AgentContext = RequestRuntimeContext & {
  agentId: string;
};

export type AgentExecution = {
  id: string;
  agentId: string;
  status: AgentStatus;
  startedAt: string;
  finishedAt?: string;
  heartbeatAt: string;
};

export type AgentResult = {
  executionId: string;
  success: boolean;
  summary: string;
};

export class AgentQueue {
  private readonly queue: Agent[] = [];

  enqueue(agent: Agent): void {
    this.queue.push(agent);
  }
  dequeue(): Agent | undefined {
    return this.queue.shift();
  }
  list(): Agent[] {
    return [...this.queue];
  }
}

export class AgentRuntime {
  private readonly agents = new Map<string, Agent>();
  private readonly history: AgentExecution[] = [];
  readonly queue = new AgentQueue();

  constructor(
    private readonly eventBus: EventBus,
    private readonly observability: ObservabilityClient,
  ) {}

  register(agent: Omit<Agent, "status"> & { status?: AgentStatus }): Agent {
    const registered: Agent = { ...agent, status: agent.status ?? "idle" };
    this.agents.set(registered.id, registered);
    return registered;
  }

  async start(
    agentId: string,
    context: RequestRuntimeContext,
  ): Promise<AgentExecution> {
    const agent = this.agents.get(agentId);
    if (!agent) {
      throw new Error(`Agent not found: ${agentId}`);
    }
    const now = new Date().toISOString();
    const execution: AgentExecution = {
      id: createId("agent_exec"),
      agentId,
      status: "running",
      startedAt: now,
      heartbeatAt: now,
    };
    this.history.push(execution);
    agent.status = "running";
    await this.eventBus.publish({
      id: createId("event"),
      name: "AgentStarted",
      payload: execution,
      timestamp: now,
      correlationId: context.correlationId,
    });
    await this.observability.event({
      category: "agent",
      eventName: "agent_started",
      message: `${agent.name} started.`,
      correlationId: context.correlationId,
      agentId,
    });
    return execution;
  }

  async finish(
    executionId: string,
    success: boolean,
    summary: string,
    context: RequestRuntimeContext,
  ): Promise<AgentResult> {
    const execution = this.history.find((item) => item.id === executionId);
    if (!execution) {
      throw new Error(`Agent execution not found: ${executionId}`);
    }
    execution.status = success ? "completed" : "failed";
    execution.finishedAt = new Date().toISOString();
    const result = { executionId, success, summary };
    await this.eventBus.publish({
      id: createId("event"),
      name: "AgentFinished",
      payload: result,
      timestamp: execution.finishedAt,
      correlationId: context.correlationId,
    });
    return result;
  }

  historyList(): AgentExecution[] {
    return [...this.history];
  }
}
